import DataGrid, { Column, Editing, FilterRow, Lookup, Paging, RequiredRule, SearchPanel } from 'devextreme-react/data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody } from 'reactstrap';
import { useAuth } from '../../../auth';
import Breadcrumb from '../../../components/common/breadcrumb';
import { getTrainJourneys, getTrainRoutes, getTrainServices, saveTrainRoute, saveTrainService } from '../../devir-teslim/api';

// Tanımlamalar > Trenler: a train service (company + capacity) is defined here first, then picked when a journey is
// planned (Sevkiyat İşlemleri > Tren Seferleri). The company is one with the role DEMIRYOLU (Tanımlamalar > Firmalar).
// Below it, routes: the service's company determines which load -> unload pairs it is allowed to run; a route must be
// defined here before it is selectable when planning a journey.
// Role group 'Sevkiyat İşlemleri - Tren Planlama' (same as TrenPlanlama.js; 056/061_train_routes_permissions.sql).
const ROLE_GROUP = 43;

const Trenler = () => {
    const { hasRole } = useAuth(ROLE_GROUP);
    const canEdit = hasRole('tren_sefer_write');

    const [services, setServices] = useState({ train_services: [], operators: [] });
    const loadServices = useCallback(() => getTrainServices()
        .then(setServices)
        .catch((e) => toast.error(e?.response?.data?.error || 'Trenler alınamadı')), []);
    useEffect(() => { loadServices(); }, [loadServices]);
    const persistService = (row) => saveTrainService({
        id: row.id || 0, name: row.name || '', operator_party_id: row.operator_party_id || 0,
        car_capacity: Number(row.car_capacity) || 0, is_active: row.is_active !== false,
    })
        .then(() => { toast.success('Kaydedildi'); loadServices(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });

    const [places, setPlaces] = useState([]);
    useEffect(() => {
        getTrainJourneys(false).then((d) => setPlaces(d.places || [])).catch((e) => toast.error(e?.response?.data?.error || 'Noktalar alınamadı'));
    }, []);

    const [routes, setRoutes] = useState([]);
    const loadRoutes = useCallback(() => getTrainRoutes(0)
        .then((d) => setRoutes(d.routes || []))
        .catch((e) => toast.error(e?.response?.data?.error || 'Rotalar alınamadı')), []);
    useEffect(() => { loadRoutes(); }, [loadRoutes]);
    const persistRoute = (row) => saveTrainRoute({
        id: row.id || 0, operator_party_id: row.operator_party_id || 0,
        load_location_id: row.load_location_id || 0, unload_location_id: row.unload_location_id || 0,
        is_active: row.is_active !== false,
    })
        .then(() => { toast.success('Kaydedildi'); loadRoutes(); return false; })
        .catch((e) => { toast.error(e?.response?.data?.error || 'Kaydedilemedi'); return true; });

    return (
        <>
            <Breadcrumb parent="Tanımlamalar" current="Trenler" />
            <Card><CardBody>
                <div className="text-muted mb-2">
                    Tren önce burada tanımlanır, sefer planlanırken listeden seçilir. İşletmeci, Firmalar'da <b>DEMİRYOLU</b> rolü
                    olan firmalardan seçilir (listede yoksa önce orada ekleyin). Trenin işletmecisi, aşağıdaki rotalar grubunda
                    hangi yükleme / boşaltma güzergahlarını çalıştırabileceğini belirler.
                </div>
                <DataGrid dataSource={services.train_services} keyExpr="id" showBorders columnAutoWidth wordWrapEnabled
                    onRowUpdating={(e) => { e.cancel = persistService({ ...e.oldData, ...e.newData }); }}
                    onRowInserting={(e) => { e.cancel = persistService({ ...e.data, id: 0 }); }}
                    onInitNewRow={(e) => { e.data.is_active = true; }}>
                    <Editing mode="row" allowUpdating={canEdit} allowAdding={canEdit} />
                    <Paging pageSize={25} />
                    <FilterRow visible />
                    <SearchPanel visible />
                    <Column dataField="name" caption="Tren adı"><RequiredRule /></Column>
                    <Column dataField="operator_party_id" caption="İşletmeci">
                        <Lookup dataSource={services.operators} valueExpr="id" displayExpr="name" />
                        <RequiredRule />
                    </Column>
                    <Column dataField="car_capacity" caption="Araç kapasitesi" dataType="number" width={130} />
                    <Column dataField="journeys" caption="Sefer" dataType="number" width={80} allowEditing={false} />
                    <Column dataField="is_active" caption="Aktif" dataType="boolean" width={80} />
                </DataGrid>
            </CardBody></Card>
            <Card><CardBody>
                <div className="text-muted mb-2">
                    Rotalar, her işletmecinin çalıştırabileceği yükleme -&gt; boşaltma güzergahlarıdır. Bir sefer planlanırken yükleme
                    ve boşaltma noktaları, trenin işletmecisi için burada tanımlı rotalarla sınırlı tutulur; kullanılmayan rotayı pasif yapın.
                </div>
                <DataGrid dataSource={routes} keyExpr="id" showBorders columnAutoWidth wordWrapEnabled
                    onRowUpdating={(e) => { e.cancel = persistRoute({ ...e.oldData, ...e.newData }); }}
                    onRowInserting={(e) => { e.cancel = persistRoute({ ...e.data, id: 0 }); }}
                    onInitNewRow={(e) => { e.data.is_active = true; }}
                    onEditorPreparing={(e) => {
                        // Editing an existing route's company/location would upsert a new (operator, load, unload)
                        // tuple on the backend rather than updating the row, leaving the old route active as an
                        // orphaned duplicate. So only is_active is editable once the row has a real id.
                        if (e.parentType === 'dataRow' && e.row && e.row.data.id
                            && ['operator_party_id', 'load_location_id', 'unload_location_id'].includes(e.dataField)) {
                            e.editorOptions.disabled = true;
                        }
                    }}>
                    <Editing mode="row" allowUpdating={canEdit} allowAdding={canEdit} />
                    <Paging pageSize={25} />
                    <FilterRow visible />
                    <SearchPanel visible />
                    <Column dataField="operator_party_id" caption="İşletmeci">
                        <Lookup dataSource={services.operators} valueExpr="id" displayExpr="name" />
                        <RequiredRule />
                    </Column>
                    <Column dataField="load_location_id" caption="Yükleme noktası">
                        <Lookup dataSource={places} valueExpr="id" displayExpr="name" />
                        <RequiredRule />
                    </Column>
                    <Column dataField="unload_location_id" caption="Boşaltma noktası">
                        <Lookup dataSource={places} valueExpr="id" displayExpr="name" />
                        <RequiredRule />
                    </Column>
                    <Column dataField="is_active" caption="Aktif" dataType="boolean" width={80} />
                </DataGrid>
            </CardBody></Card>
        </>
    );
};

export default Trenler;
